import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { getLocalizedPath } from "@/lib/getLocalizedPath";
import { useRef } from "react";
import { openWhatsApp, WHATSAPP_MESSAGES } from "@/lib/whatsapp";
import { OptimizedPicture } from "@/components/OptimizedPicture";

export const Hero = () => {
  const { language, translations } = useLanguage();
  const t = translations?.hero;
  const ref = useRef(null);
  
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"]
  });
  
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);

  // Show minimal content while translations load
  if (!t) {
    return (
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 w-full h-full">
          <OptimizedPicture
            src="/lovable-uploads/b01b136c-e0eb-4a4a-aa74-0d26afd9c08f.png"
            alt="Montessori classroom environment"
            width={1335}
            height={890}
            loading="eager"
            fetchPriority="high"
            decoding="sync"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-transparent" />
        </div>
      </section>
    );
  }

  return (
    <section ref={ref} className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0 w-full h-full">
        <OptimizedPicture
          src="/lovable-uploads/b01b136c-e0eb-4a4a-aa74-0d26afd9c08f.png"
          alt="Montessori classroom environment"
          width={1335}
          height={890}
          loading="eager"
          fetchPriority="high"
          decoding="sync"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-transparent" />
      </div>
      
      <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-4xl md:text-6xl font-light text-white mb-6"
        >
          {t.title}
        </motion.h1>
        
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-xl md:text-2xl text-white/95 mb-12 font-light"
        >
          {t.subtitle}
        </motion.p>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="flex flex-col sm:flex-row gap-4 justify-center items-center"
        >
          <button
            onClick={() => openWhatsApp(WHATSAPP_MESSAGES.bookVisit[language])}
            className="bg-primary text-primary-foreground px-8 py-4 rounded-lg font-medium hover:bg-primary/90 transition-all duration-200 shadow-xl hover:shadow-2xl hover:scale-105"
          >
            {t.bookVisit}
          </button>
          
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <ArrowDown className="w-8 h-8 text-white/70" />
        </motion.div>
      </motion.div>
    </section>
  );
};
